import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  WSActions,
  WSCard,
  WSControl,
  WSFlexBox,
  WSInfoBox,
  WSList,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import { WSQueries } from "../../../../query/WSQuery";
import { useFlowContext } from "../../../../shared/components/Flow";
import { useQueryMeridioPlanCatalog } from "../../../../query/benefits/useQueryMeridioPlanCatalog";
import {
  DefaultPaymentMethod,
  useDefaultPaymentMethod
} from "../../../Settings/components/payment/DefaultPaymentMethod";
import {
  PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD,
  PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYMENT_METHOD
} from "../../../Settings/routes/RoutePaymentMethods";
import {
  IMeridioPlanCatalogCategory,
  IMeridioPlanCatalogResponse,
  IMeridioPlanOption
} from "../../../../services/api/benefits/meridioPlanCatalog/types";
import { CoverageTier, EnrollMeridioFlowState } from "./types";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

const TIER_KEY_MAP: Record<CoverageTier, "io" | "is" | "ic" | "if"> = {
  IO: "io",
  IS: "is",
  IC: "ic",
  IF: "if"
};

function findPlan(
  categories: IMeridioPlanCatalogCategory[],
  baseSku: string
): IMeridioPlanOption | undefined {
  for (const cat of categories) {
    const found = (cat.plans as IMeridioPlanOption[]).find(
      p => p.baseSku === baseSku
    );
    if (found) return found;
  }
  return undefined;
}

export const StepSetupPayment: React.FC<FlowStep> = props => {
  const history = useHistory();
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();
  const [performDeduction, setPerformDeduction] = useState(
    flowState.performDeduction ?? true
  );

  const queryMeridioPlanCatalog = useQueryMeridioPlanCatalog();
  const defaultPaymentMethod = useDefaultPaymentMethod();

  const allSelectedSkus = useMemo(
    () =>
      [
        flowState.healthPlanSku,
        ...(flowState.voluntaryPlanSkus ?? []),
        flowState.dentalVisionPlanSku
      ].filter((s): s is string => !!s),
    [
      flowState.healthPlanSku,
      flowState.voluntaryPlanSkus,
      flowState.dentalVisionPlanSku
    ]
  );

  const coverageStartDate = useMemo(() => {
    const now = new Date();
    const monthsToAdd = now.getDate() <= 10 ? 1 : 2;
    return new Date(now.getFullYear(), now.getMonth() + monthsToAdd, 1);
  }, []);

  const firstPaymentDate = useMemo(() => {
    return new Date(
      coverageStartDate.getFullYear(),
      coverageStartDate.getMonth() - 1,
      19
    );
  }, [coverageStartDate]);

  const handleContinue = () => {
    setFlowState(prev => ({ ...prev, performDeduction }));
    props.onNext?.();
  };

  return (
    <>
      <WSText.Heading3 weight="medium" mb="S">
        Setup payment
      </WSText.Heading3>
      <WSText.ParagraphSm weight="book" color="gray500" mb="XL">
        Confirm your payment method or add a new one.
      </WSText.ParagraphSm>

      <WSList gap="XL">
        <DefaultPaymentMethod
          helperText=""
          onAddNew={() =>
            history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD)
          }
          onManage={() =>
            history.push(PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYMENT_METHOD)
          }
        />

        <WSQueries queries={{ queryMeridioPlanCatalog }}>
          {({
            queryMeridioPlanCatalogData
          }: {
            queryMeridioPlanCatalogData: IMeridioPlanCatalogResponse;
          }) => {
            const totalMonthlyPremium = allSelectedSkus.reduce(
              (sum, baseSku) => {
                const tier = flowState.planSelections?.[baseSku];
                if (!tier) return sum;
                const tierKey = TIER_KEY_MAP[tier];
                const plan = findPlan(
                  queryMeridioPlanCatalogData.categories,
                  baseSku
                );
                return sum + (plan?.[tierKey]?.monthlyPremium ?? 0);
              },
              0
            );

            return (
              <WSCard>
                <WSFlexBox.CenterY justify="space-between" mb="M">
                  <WSText.ParagraphSm weight="medium" color="gray500">
                    Your monthly premium:
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm weight="medium" color="gray700">
                    {formatMoney(totalMonthlyPremium, Currency.USD)}
                  </WSText.ParagraphSm>
                </WSFlexBox.CenterY>
                <WSInfoBox colorBackground="gray50">
                  <WSText.ParagraphXs color="gray600" weight="medium" mb="XS">
                    Coverage start date
                  </WSText.ParagraphXs>
                  <WSText.ParagraphXs weight="book" mb="M" color="gray600">
                    {toWSDateString(coverageStartDate, "monthDayYear")}
                  </WSText.ParagraphXs>
                  <WSText.ParagraphXs weight="book" color="gray500">
                    Note: your coverage will not begin until the first payment
                    is successfully processed by{" "}
                    {toWSDateString(firstPaymentDate, "monthDayYear")}. If the
                    payment doesn't go through, we'll automatically retry daily
                    from 12-19th of the month. If payment fails, your enrollment
                    will be automatically cancelled and you must return to this
                    step to enter a valid payment method to enroll again.
                  </WSText.ParagraphXs>
                </WSInfoBox>
              </WSCard>
            );
          }}
        </WSQueries>

        <WSCard>
          <WSText.Paragraph weight="medium" color="gray500" mb="XS">
            Future monthly fees
          </WSText.Paragraph>
          <WSText.ParagraphXs weight="book" color="gray400" mb="L">
            Choose how your monthly fee will be paid going forward.
          </WSText.ParagraphXs>
          <WSList gap="L">
            <WSControl
              type="radio"
              value={performDeduction === true}
              onChange={() => setPerformDeduction(true)}
              label="Deductions (Recommended)"
              helperText="Automatically deduct your monthly fee from your earnings.* Preferred by most enrollees."
            />
            <WSControl
              type="radio"
              value={performDeduction === false}
              onChange={() => setPerformDeduction(false)}
              label="Payment method"
              helperText="Pay my monthly fee using my default payment method"
            />
            <WSInfoBox>
              <WSText.ParagraphXs color="gray600" weight="medium">
                *Automatic payments
              </WSText.ParagraphXs>
              <WSText.ParagraphXs color="gray600" weight="book">
                We will first attempt to deduct your monthly fee from your
                earnings. If insufficient, your default payment method will be
                charged.
              </WSText.ParagraphXs>
            </WSInfoBox>
          </WSList>
        </WSCard>
      </WSList>

      <WSActions
        mt="XL"
        alignment="fill"
        buttons={[
          { kind: "Tertiary", label: "Back", onClick: props.onBack },
          {
            label: "Continue",
            onClick: handleContinue,
            disabled: !defaultPaymentMethod.id
          }
        ]}
      />
    </>
  );
};
